/**
 * Stub / placeholder pages for the Food Pulse mobile shell — these
 * satisfy the routes required by the bottom-nav so navigation
 * (News/Events · My Lists · My Deals · Recipes · Groups) and the
 * home-tile shortcuts (Products · Stores · Charities) always resolve
 * to a real screen while the fuller UI is built out screen-by-screen
 * as the user provides more mockups.
 *
 * Each stub renders a friendly heading + short description; the ones
 * that require a logged-in user (My Lists / My Deals / Groups /
 * Profile) render the AuthGateModal for guests.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import AuthGateModal from "./AuthGateModal";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";

function ScreenHeading({ title, subtitle }) {
  return (
    <div className="px-5 pt-5 pb-3">
      <h1 className="text-2xl font-bold" style={{ color: FP_PURPLE }}>{title}</h1>
      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
    </div>
  );
}

function ComingSoon({ children }) {
  return (
    <div className="mx-4 mt-4 py-10 text-center bg-gray-100 rounded-2xl">
      <div className="text-4xl mb-3">🍽️</div>
      <div className="font-semibold">Coming soon</div>
      <div className="text-xs opacity-60 mt-1 max-w-xs mx-auto">{children}</div>
    </div>
  );
}

/* ---------------- NEWS / EVENTS (public — sp_articles) ---------------- */

export function MobileNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/sp-articles`, { params: { site_source: "food-pulse", limit: 20 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="mobile-news-page">
      <ScreenHeading title="News & Events" subtitle="Food-industry stories, waste-reduction wins, and upcoming events near you." />
      {loading ? (
        <div className="px-5 text-sm opacity-60">Loading…</div>
      ) : items.length === 0 ? (
        <ComingSoon>Fresh stories are on the way.</ComingSoon>
      ) : (
        <ul className="px-4 space-y-3">
          {items.map((a) => (
            <li key={a.slug} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <Link to={`/food-pulse/articles/${a.slug}`} className="block" data-testid={`news-item-${a.slug}`}>
                {a.feature_image && (
                  <img src={a.feature_image} alt={a.title} className="w-full h-40 object-cover" loading="lazy" />
                )}
                <div className="p-4">
                  <div className="text-base font-bold">{a.title}</div>
                  {a.excerpt && <div className="text-sm mt-1 opacity-70 line-clamp-2">{a.excerpt}</div>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------- MY DEALS (auth — saved / favourited) ---------------- */

export function MobileMyDeals() {
  const { user } = useAuth() || {};
  if (!user) {
    return (
      <div className="relative min-h-[60vh]" data-testid="mobile-mydeals">
        <ScreenHeading title="My Deals" subtitle="Deals, coupons, and surplus alerts you've saved." />
        <AuthGateModal />
      </div>
    );
  }
  return (
    <div data-testid="mobile-mydeals">
      <ScreenHeading title="My Deals" subtitle="Deals, coupons, and surplus alerts you've saved." />
      <ComingSoon>Save deals from the Deals tab to see them here.</ComingSoon>
    </div>
  );
}

/* ---------------- GROUPS / CHATS (auth) ---------------- */

export function MobileGroups() {
  const { user } = useAuth() || {};
  if (!user) {
    return (
      <div className="relative min-h-[60vh]" data-testid="mobile-groups">
        <ScreenHeading title="Groups & Chats" subtitle="Neighbourhood food-swap groups, cooking circles, and direct chats." />
        <AuthGateModal />
      </div>
    );
  }
  return (
    <div data-testid="mobile-groups">
      <ScreenHeading title="Groups & Chats" subtitle="Neighbourhood food-swap groups, cooking circles, and direct chats." />
      <ComingSoon>Join a group to start chatting with fellow food lovers.</ComingSoon>
    </div>
  );
}

/* ---------------- ABOUT (public) ---------------- */

export function MobileAbout() {
  return (
    <div data-testid="mobile-about">
      <ScreenHeading title="About Food Pulse" subtitle="Social Food Discovery." />
      <div className="px-5 space-y-4 text-sm leading-relaxed">
        <p>Food Pulse connects supermarkets, grocers, restaurants, and caterers with local food banks, soup kitchens, and everyday households — reducing food waste while helping communities eat well.</p>
        <p>Every year, $165 billion worth of edible food goes to waste in the U.S. alone. Together, we can change that.</p>
        <p>Build Good Karma, create Social Capital, and help feed a better future.</p>
      </div>
    </div>
  );
}
